import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import {
  getMcpClientMeta,
  type McpClientId,
} from "@arcadeai/ui-kit/mcp-gateway-connect";
import { ArrowRight, Copy } from "lucide-react";
import type { JSX } from "react";
import { buildPromptDeepLink } from "@/app/onboarding-first-win/lib/prompt-deep-links";

type OnboardingPromptCtasProps = {
  clientId: McpClientId;
  prompt: string;
  onPromptCopy: () => void;
  onPromptDeepLink: () => void;
};

function getOpenChatLabel(clientId: McpClientId, name: string): string {
  if (clientId === "claude_code") {
    return `Open ${name} with this message`;
  }
  return `Open a chat in ${name}`;
}

export function OnboardingPromptCtas({
  clientId,
  prompt,
  onPromptCopy,
  onPromptDeepLink,
}: OnboardingPromptCtasProps): JSX.Element {
  const { name, logo } = getMcpClientMeta(clientId);
  const promptDeepLink = buildPromptDeepLink(clientId, prompt);

  if (promptDeepLink) {
    return (
      <div className="space-y-3">
        <a
          className="group flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border/60 bg-card px-3 py-3 transition-colors hover:border-border hover:bg-muted/30"
          href={promptDeepLink}
          onClick={onPromptDeepLink}
          rel="noopener noreferrer"
          target="_blank"
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border/60 bg-background [&>svg]:size-5">
            {logo}
          </span>
          <span className="min-w-0 flex-1">
            <span className="line-clamp-2 font-medium text-foreground text-sm leading-snug">
              &ldquo;{prompt}&rdquo;
            </span>
            <span className="mt-1 block text-muted-foreground text-xs">
              {getOpenChatLabel(clientId, name)}
            </span>
          </span>
          <ArrowRight
            aria-hidden
            className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
          />
        </a>
        <CopyButton
          className="inline-flex h-auto w-auto gap-1.5 px-0 font-normal text-muted-foreground text-xs hover:bg-transparent hover:text-foreground"
          content={prompt}
          onCopy={onPromptCopy}
          size="sm"
          variant="ghost"
        >
          <Copy aria-hidden className="size-3.5" />
          Copy message instead
        </CopyButton>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="font-medium text-muted-foreground text-xs">
            Suggested first message
          </p>
          <CopyButton
            className="size-7 shrink-0"
            content={prompt}
            onCopy={onPromptCopy}
            size="icon-sm"
            variant="ghost"
          />
        </div>
        <p className="text-foreground text-sm leading-relaxed">
          &ldquo;{prompt}&rdquo;
        </p>
      </div>
      <p className="text-muted-foreground text-xs">
        Copy the message above and paste it into {name}.
      </p>
    </div>
  );
}
